import { Close, Search } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, OutlinedInput, CircularProgress } from '@mui/material';
import { useSearchContentQuery } from 'client/modules/Help/helpSlice';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  helpSearchBoxContainerSx,
  helpSearchInputSx,
  helpSearchResultsDropdownSx,
} from './helpSsrStyles';

function SearchBox({ locale, setSearchResults, setSearching, containerSx, size }) {
  const { formatMessage } = useIntl();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const skip = !debouncedQuery || debouncedQuery.trim().length < 2;
  const { data: searchResults, isFetching } = useSearchContentQuery(
    { query: debouncedQuery, locale },
    { skip },
  );

  useEffect(() => {
    setIsSearching(isFetching);
  }, [isFetching]);

  useEffect(() => {
    if (typeof setSearchResults !== 'function') {
      return;
    }
    if (skip) {
      setSearchResults([]);
    } else {
      setSearchResults(searchResults);
    }
  }, [searchResults, setSearchResults, skip]);

  useEffect(() => {
    if (typeof setSearching === 'function') {
      setSearching(isSearching);
    }
  }, [isSearching, setSearching]);

  return (
    <Box sx={containerSx}>
      <OutlinedInput
        size={size}
        sx={helpSearchInputSx}
        placeholder={formatMessage({
          id: 'help.search.placeholder',
          defaultMessage: 'Search help articles...',
        })}
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        endAdornment={
          <InputAdornment position="end">
            {isSearching ? (
              <CircularProgress size={20} />
            ) : searchQuery ? (
              <IconButton onClick={() => setSearchQuery('')} edge="end" size="small">
                <Close />
              </IconButton>
            ) : null}
          </InputAdornment>
        }
      />

      {!setSearchResults && !skip && searchQuery && (
        <Box sx={helpSearchResultsDropdownSx}>
          {isSearching ? (
            <Box className="help-search-dropdown-title">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <FormattedMessage id="help.search.searching" defaultMessage="Searching..." />
              </Box>
            </Box>
          ) : searchResults?.results ? (
            <>
              <Box className="help-search-dropdown-title">
                <FormattedMessage
                  id="help.search.results"
                  defaultMessage="Search Results ({count})"
                  values={{ count: searchResults.results.length }}
                />
              </Box>
              {searchResults.results.slice(0, 5).map(result => (
                <Link
                  key={result.slug}
                  to={`/help/${result.categorySlug}/${result.slug}`}
                  className="help-search-dropdown-link"
                  onClick={() => setSearchQuery('')}
                >
                  <Box className="help-search-dropdown-result-title">
                    {result.title || result.slug}
                  </Box>
                  {result.highlightedExcerpt && (
                    <Box
                      className="help-search-dropdown-excerpt"
                      dangerouslySetInnerHTML={{
                        __html: result.highlightedExcerpt,
                      }}
                    />
                  )}
                </Link>
              ))}
            </>
          ) : null}
        </Box>
      )}
    </Box>
  );
}

SearchBox.defaultProps = {
  containerSx: helpSearchBoxContainerSx,
  size: 'small',
};

SearchBox.propTypes = {
  locale: PropTypes.string.isRequired,
  setSearchResults: PropTypes.func,
  setSearching: PropTypes.func,
  containerSx: PropTypes.object,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

export default SearchBox;
